'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import styles from './DialogCompat.module.css';
import {
  Dialog as UiDialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent as UiDialogContent,
  DialogHeader,
  DialogTitle as UiDialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/ui/components/Dialog';

type CloseReason = 'backdropClick' | 'escapeKeyDown';

type LegacyDialogProps = {
  open: boolean;
  onClose?: (event?: unknown, reason?: CloseReason) => void;
  children?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullScreen?: boolean;
  fullWidth?: boolean;
  className?: string;
  PaperProps?: {
    className?: string;
    style?: React.CSSProperties;
    sx?: SxProps<Theme>;
  };
  disableEscapeKeyDown?: boolean;
  // Accepted for legacy MUI call-site compatibility; not applied by the base-ui
  // Dialog (focus management is handled by the primitive).
  disableAutoFocus?: boolean;
  disableEnforceFocus?: boolean;
  TransitionComponent?: React.ElementType;
  TransitionProps?: unknown;
  keepMounted?: boolean;
  scroll?: 'body' | 'paper';
  sx?: SxProps<Theme>;
};

const sizeMap: Record<string, 'sm' | 'md' | 'lg' | 'xl'> = {
  xs: 'sm',
  sm: 'md',
  md: 'lg',
  lg: 'xl',
  xl: 'xl',
};

const muiWidthClass = (maxWidth: LegacyDialogProps['maxWidth']) => {
  if (maxWidth === false) return 'False';
  const value = maxWidth ?? 'sm';
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
};

/**
 * Compatibility shell for legacy MUI dialog call sites. The behaviour-facing
 * API stays stable while rendering the canonical styleguide Dialog primitives.
 */
export function Dialog({
  open,
  onClose,
  children,
  maxWidth = 'sm',
  fullScreen = false,
  fullWidth = false,
  className,
  PaperProps,
  sx,
}: LegacyDialogProps) {
  const size = fullScreen || maxWidth === false ? 'full' : sizeMap[maxWidth] ?? 'md';

  return (
    <UiDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose?.(undefined, 'backdropClick');
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <Box className={className} sx={sx}>
          <Box
            component={UiDialogContent}
            size={size}
            className={[
              'MuiDialog-paper',
              styles.legacyContent,
              `MuiDialog-paperWidth${muiWidthClass(maxWidth)}`,
              fullWidth ? 'MuiDialog-paperFullWidth' : '',
              fullScreen ? 'MuiDialog-paperFullScreen' : '',
              PaperProps?.className,
            ].filter(Boolean).join(' ')}
            sx={PaperProps?.sx}
            style={PaperProps?.style}
          >
            {children}
          </Box>
        </Box>
      </DialogPortal>
    </UiDialog>
  );
}

export const DialogTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { sx?: SxProps<Theme> }
>(function DialogTitle({ children, className, sx, ...props }, ref) {
  return (
    <Box
      component={DialogHeader}
      ref={ref}
      className={['MuiDialogTitle-root', className].filter(Boolean).join(' ')}
      sx={sx}
      {...props}
    >
      <UiDialogTitle>{children}</UiDialogTitle>
    </Box>
  );
});

export const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { dividers?: boolean; sx?: SxProps<Theme> }
>(function DialogContent({ className, dividers: _dividers, sx, ...props }, ref) {
  return <Box component={DialogBody} ref={ref} className={['MuiDialogContent-root', className].filter(Boolean).join(' ')} sx={sx} {...props} />;
});

export const DialogActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { disableSpacing?: boolean; sx?: SxProps<Theme> }
>(function DialogActions({ className, disableSpacing: _disableSpacing, sx, ...props }, ref) {
  return <Box component={DialogFooter} ref={ref} className={['MuiDialogActions-root', className].filter(Boolean).join(' ')} sx={sx} {...props} />;
});

export const DialogContentText = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement> & { sx?: SxProps<Theme> }
>(function DialogContentText({ className, sx, ...props }, ref) {
  return <Box component={DialogDescription} ref={ref} className={['MuiDialogContentText-root', className].filter(Boolean).join(' ')} sx={sx} {...props} />;
});
